import type { AnchorHTMLAttributes, ButtonHTMLAttributes, JSX, ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router';

import { buttonClasses, type ButtonSize, type ButtonVariant } from '@/components/ui/buttonStyles';
import { ExternalLinkIcon } from '@/components/ui/icons';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  type?: 'button' | 'submit' | 'reset';
  children: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  className,
  type = 'button',
  children,
  ...props
}: ButtonProps): JSX.Element {
  return (
    <button type={type} className={buttonClasses(variant, size, className)} {...props}>
      {children}
    </button>
  );
}

interface ButtonLinkProps extends LinkProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

/** Internal navigation styled as a button. */
export function ButtonLink({
  variant = 'primary',
  size = 'md',
  className,
  children,
  ...props
}: ButtonLinkProps): JSX.Element {
  return (
    <Link className={buttonClasses(variant, size, className)} {...props}>
      {children}
    </Link>
  );
}

interface ExternalButtonLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Set false for mailto:/tel: links, which do not open a new browsing context. */
  opensInNewTab?: boolean;
}

export function ExternalButtonLink({
  variant = 'secondary',
  size = 'md',
  className,
  opensInNewTab = true,
  children,
  ...props
}: ExternalButtonLinkProps): JSX.Element {
  return (
    <a
      className={buttonClasses(variant, size, className)}
      {...(opensInNewTab ? { target: '_blank', rel: 'noreferrer noopener' } : {})}
      {...props}
    >
      {children}
      {opensInNewTab ? (
        <>
          <ExternalLinkIcon width={16} height={16} />
          <span className="sr-only">(opens in a new tab)</span>
        </>
      ) : null}
    </a>
  );
}
